// Function condition card (schema mode) — authors a HAVING-style criterion:
// a dialect-supported aggregate/statistical function applied to a column
// (drag one from the schema tree into the slot) or to a free-hand typed
// expression, compared against a fixed value or a runtime prompt.
//
// Engine contract (bi_sql.source_sql._render_leaf): the rule carries its own
// `FUNCTION` (canonical name — the engine spells it per dialect, e.g.
// COUNT_DISTINCT → COUNT(DISTINCT x), STDDEV → STDEV on SQL Server) and/or a
// free-hand `expression`; `applyTo: 'having'` routes it into the HAVING
// clause even when the engine cannot detect the aggregate in the expression.

import { useState } from 'react'
import { Sigma, X } from 'lucide-react'
import type { VisualFunction } from '@/api/visualData'
import type { CriteriaRule } from './types'
import type { BankColumn } from './dataSource'

const selectStyle: React.CSSProperties = {
  padding: '4px 7px', fontSize: 13.5, borderRadius: 7,
  border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)',
  color: 'var(--color-text)', maxWidth: '100%',
}
const inputStyle: React.CSSProperties = { ...selectStyle }
const OPERATORS = ['=', '!=', '>', '>=', '<', '<='] as const

export type DragItemLike = { kind: 'bank'; key: string } | { kind: string }

export default function FunctionCriteriaCard({
  rule, functions, bank, dragItem, onChange, onDelete,
}: {
  rule: CriteriaRule
  /** Dialect-supported functions (aggregate + statistical; windows excluded —
   *  a window function is not valid in HAVING). */
  functions: VisualFunction[]
  bank: BankColumn[]
  dragItem: DragItemLike | null
  onChange: (field: keyof CriteriaRule, value: unknown) => void
  onDelete: () => void
}) {
  const [slotActive, setSlotActive] = useState(false)
  const freehand = typeof rule.expression === 'string' && rule.expression !== undefined
    && (rule.expression !== '' || !rule.field)
  const [freehandMode, setFreehandMode] = useState<boolean>(
    () => Boolean((rule.expression || '').trim()),
  )
  const isPrompt = (rule.queryOption || 'F').toUpperCase() === 'P'
  const hasColumn = Boolean((rule.tableName || '') && (rule.DBNAME || ''))

  const dropColumn = (key: string) => {
    const src = bank.find((b) => b.key === key)
    if (!src) return
    onChange('field', `${src.DBTABLE || ''}.${src.DBNAME || ''}`)
    onChange('tableName', src.DBTABLE || '')
    onChange('DBNAME', src.DBNAME || '')
    // Aggregated comparisons are numeric except MIN/MAX, which keep the
    // column's own type (e.g. MAX(ORDER_DATE) compared to a date).
    const fn = (rule.FUNCTION || '').toUpperCase()
    const t = fn === 'MIN' || fn === 'MAX' ? src.type : 'number'
    onChange('COLTYPE', t)
    onChange('currentFieldType', t)
    onChange('expression', '')
  }

  return (
    <div style={{
      border: '1px solid var(--color-border-strong)', borderRadius: 9,
      padding: 10, background: 'var(--color-bg)', fontSize: 13.5,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Sigma size={13} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
        <span style={{ fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', fontSize: 13, letterSpacing: 0.4 }}>
          Function condition
        </span>
        <span style={{ color: 'var(--color-text-faint)', fontSize: 13 }}>
          applies after aggregation (HAVING)
        </span>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={onDelete} title="Remove this condition">
          <X size={13} />
        </button>
      </div>

      {/* function + target */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <select
          value={rule.FUNCTION || ''}
          onChange={(e) => onChange('FUNCTION', e.target.value)}
          style={selectStyle}
          title="Only functions the bound database supports are offered"
        >
          {freehandMode && <option value="">(expression as-is)</option>}
          {functions.map((f) => (
            <option key={f.name} value={f.name}>{f.label}</option>
          ))}
        </select>
        <span style={{ color: 'var(--color-text-faint)' }}>of</span>
        {freehandMode ? (
          <input
            type="text"
            value={rule.expression || ''}
            placeholder="free-hand expression, e.g. QTY * RATE"
            onChange={(e) => onChange('expression', e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 150, fontFamily: 'ui-monospace, monospace' }}
          />
        ) : (
          <div
            onDragOver={(e) => { if (dragItem?.kind === 'bank') { e.preventDefault(); e.stopPropagation(); setSlotActive(true) } }}
            onDragLeave={() => setSlotActive(false)}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setSlotActive(false)
              if (dragItem && dragItem.kind === 'bank' && 'key' in dragItem) {
                dropColumn(dragItem.key)
              }
            }}
            style={{
              flex: 1, minWidth: 150, padding: '4px 8px', borderRadius: 7,
              border: `1.5px dashed ${slotActive ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
              background: slotActive
                ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)'
                : 'var(--color-bg-secondary)',
              color: hasColumn ? 'var(--color-text)' : 'var(--color-text-faint)',
              fontFamily: hasColumn ? 'ui-monospace, monospace' : undefined,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title="Drag a column from the schema tree into this slot"
          >
            {hasColumn ? `${rule.tableName}.${rule.DBNAME}` : 'drop a column here'}
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }} title="Type the left side yourself instead of dropping a column (author-trusted SQL)">
          <input
            type="checkbox"
            checked={freehandMode}
            onChange={(e) => {
              setFreehandMode(e.target.checked)
              if (!e.target.checked) onChange('expression', '')
            }}
          />
          fx
        </label>
      </div>

      {/* comparison */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <select
          value={rule.operator || '>'}
          onChange={(e) => onChange('operator', e.target.value)}
          style={selectStyle}
        >
          {OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
        </select>
        <select
          value={isPrompt ? 'P' : 'F'}
          onChange={(e) => {
            onChange('queryOption', e.target.value)
            onChange('prevOption', e.target.value)
          }}
          style={selectStyle}
        >
          <option value="F">fixed value</option>
          <option value="P">ask at runtime</option>
        </select>
        {isPrompt && (
          <input
            type="text"
            value={rule.promptLabel || ''}
            placeholder="prompt label"
            onChange={(e) => onChange('promptLabel', e.target.value)}
            style={{ ...inputStyle, width: 120 }}
          />
        )}
        <input
          type="text"
          value={rule.value || ''}
          placeholder={isPrompt ? 'default' : 'value'}
          onChange={(e) => onChange('value', e.target.value)}
          style={{ ...inputStyle, width: 110 }}
        />
      </div>
      {Boolean(freehand && freehandMode && (rule.expression || '').trim()) && !(rule.FUNCTION || '') && (
        <div style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
          The expression is used as typed — include your own aggregate (e.g. SUM(QTY * RATE)).
        </div>
      )}
    </div>
  )
}
